import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlanAssignInput } from '@cms/models';
import { saasApi } from '../../../lib/api/saas.api';
import { Button } from '@/components/ui/button';
import { CommonModal } from '@/components/common/CommonModal';
import { useAdminI18n } from '../../../lib/i18n/use-admin-i18n';

type AssignPlanFormData = {
  tenantId: string;
  planId: string;
  startsAt: string;
};

interface AssignPlanModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSuccess?: () => void;
}

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';

export function AssignPlanModal({
  open,
  onClose,
  onSuccess,
}: AssignPlanModalProps) {
  const { page, act } = useAdminI18n();
  const queryClient = useQueryClient();

  const { data: tenants = [] } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => saasApi.getTenants(),
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['plans'],
    queryFn: () => saasApi.getPlans(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AssignPlanFormData>({
    defaultValues: {
      tenantId: '',
      planId: plans[0]?.id ?? '',
      startsAt: new Date().toISOString().slice(0, 16),
    },
  });

  const assignMutation = useMutation({
    mutationFn: async (data: AssignPlanFormData) => {
      const payload: PlanAssignInput = {
        planId: data.planId,
        startsAt: new Date(data.startsAt).toISOString(),
      };
      return saasApi.assignPlanToTenant(data.tenantId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenant-subscriptions'] });
      onSuccess?.();
      reset();
      onClose();
    },
  });

  const onSubmit = async (data: AssignPlanFormData) => {
    try {
      await assignMutation.mutateAsync(data);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : page('plans', 'assign_modal.failed_to_assign');
      alert(message);
    }
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={page('plans', 'assign_modal.title')}
      titleId="assign-plan-modal-title"
      panelClassName="max-w-lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="assign-tenantId" className="block text-sm font-medium text-foreground">
              {page('plans', 'assign_modal.tenant_label')}
            </label>
            <select
              id="assign-tenantId"
              {...register('tenantId', { required: page('plans', 'assign_modal.tenant_required') })}
              className={inputClass}
            >
              <option value="">{page('plans', 'assign_modal.select_tenant')}</option>
              {tenants.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name ?? t.code ?? t.id}
                </option>
              ))}
            </select>
            {errors.tenantId && (
              <p className="mt-1 text-sm text-destructive">{errors.tenantId.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="assign-planId" className="block text-sm font-medium text-foreground">
              {page('plans', 'assign_modal.plan_label')}
            </label>
            <select
              id="assign-planId"
              {...register('planId', { required: page('plans', 'assign_modal.plan_required') })}
              className={inputClass}
            >
              <option value="">{page('plans', 'assign_modal.select_plan')}</option>
              {plans.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
            {errors.planId && (
              <p className="mt-1 text-sm text-destructive">{errors.planId.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="assign-startsAt" className="block text-sm font-medium text-foreground">
              {page('plans', 'assign_modal.starts_at_label')}
            </label>
            <input
              id="assign-startsAt"
              type="datetime-local"
              {...register('startsAt', { required: page('plans', 'assign_modal.starts_at_required') })}
              className={inputClass}
            />
            {errors.startsAt && (
              <p className="mt-1 text-sm text-destructive">{errors.startsAt.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {act('cancel')}
            </Button>
            <Button type="submit" disabled={assignMutation.isPending}>
              {assignMutation.isPending ? page('plans', 'assign_modal.assigning') : page('plans', 'assign_modal.assign')}
            </Button>
          </div>
      </form>
    </CommonModal>
  );
}
